import React, { useState, useMemo, useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Typography, Fade, IconButton, Button } from '@material-ui/core';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { ChoiceOption } from '../types';
import { useTemplateParser } from '../hooks/ImpactAiContextProvider';
import { generateColorPaletteSet, ColorSet } from '../../../utils/colorHelpers';

interface MultipleChoiceSelectorProps {
  choices: ChoiceOption[];
  onSubmit: (selectedChoices: Array<{ key: string; choice: ChoiceOption }>) => void;
  className?: string;
  onGoBack?: () => void;
  canGoBack?: boolean;
  minSelections?: number;
  maxSelections?: number;
  submitButtonText?: string;
}

interface StyleProps {
  backgroundColor?: string;
  textColor?: string;
  iconColor?: string;
  isSelected?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  choiceContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0),
    width: '100%',
    position: 'relative',
    flexDirection: 'column',
  },
  choicesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  choiceOption: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(2),
    cursor: 'pointer',
    position: 'relative',
    transition: theme.transitions.create(['filter', 'transform', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
    border: '2px solid transparent',
    '&:hover': {
      filter: 'brightness(0.9)',
      transform: 'translateY(-2px)',
    },
  },
  tagOption: {
    margin: theme.spacing(0.25),
    padding: theme.spacing(0.5, 1.5),
  },
  selectedChoice: {
    transform: 'translateY(-2px)',
  },
  choiceText: {
    fontWeight: 500,
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(16),
  },
  icon: {
    fontSize: theme.typography.pxToRem(22),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButton: {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  backButtonIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  submitButton: {
    alignSelf: 'center',
    minWidth: theme.spacing(15),
    padding: theme.spacing(1, 3),
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  selectionCount: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(14),
    marginBottom: theme.spacing(1),
  },
}));

const useDynamicChoiceStyles = makeStyles(() => ({
  dynamicChoiceOption: {
    backgroundColor: (props: StyleProps) => props.backgroundColor || '#F6F4FB',
    boxShadow: (props: StyleProps) =>
      props.isSelected && props.textColor ? `0 0 0 2px ${props.textColor}` : 'none',
  },
  dynamicChoiceText: {
    color: (props: StyleProps) => props.textColor || 'inherit',
    fontWeight: (props: StyleProps) => (props.isSelected ? 600 : 500),
  },
  dynamicChoiceIcon: {
    color: (props: StyleProps) => props.iconColor || 'inherit',
  },
}));

interface ChoiceElementProps {
  choiceIndex: number;
  choice: ChoiceOption;
  isSelected: boolean;
  generatedColors: ColorSet[];
  onToggle: (index: number) => void;
  parseTemplate: (text: string) => string;
}

const ChoiceElement: React.FC<ChoiceElementProps> = ({
  choiceIndex,
  choice,
  isSelected,
  generatedColors,
  onToggle,
  parseTemplate,
}) => {
  const classes = useStyles({} as StyleProps);
  const parsedText = parseTemplate(choice.text);
  const colors = generatedColors[choiceIndex];

  const dynamicClasses = useDynamicChoiceStyles({
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
    iconColor: colors.iconColor,
    isSelected,
  });

  return (
    <div
      className={`${classes.choiceOption} ${dynamicClasses.dynamicChoiceOption} ${
        classes.tagOption
      } ${isSelected ? classes.selectedChoice : ''}`}
      onClick={() => onToggle(choiceIndex)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(choiceIndex);
        }
      }}
    >
      {choice.icon && (
        <span className={`${classes.icon} ${dynamicClasses.dynamicChoiceIcon}`}>{choice.icon}</span>
      )}
      <Typography className={`${classes.choiceText} ${dynamicClasses.dynamicChoiceText}`}>
        {parsedText}
      </Typography>
    </div>
  );
};

const MultipleChoiceSelector: React.FC<MultipleChoiceSelectorProps> = ({
  choices,
  onSubmit,
  className,
  onGoBack,
  canGoBack = false,
  minSelections = 1,
  maxSelections,
  submitButtonText = 'Continue',
}) => {
  const classes = useStyles({} as StyleProps);
  const [visible, setVisible] = useState(true);
  const [selectedChoices, setSelectedChoices] = useState<Set<number>>(new Set());
  const parseTemplate = useTemplateParser();

  // Generate enhanced color palette for choices
  const generatedColors = useMemo(
    (): ColorSet[] => generateColorPaletteSet(choices.length),
    [choices]
  );

  const handleChoiceToggle = useCallback(
    (index: number) => {
      setSelectedChoices((prev) => {
        const newSelected = new Set(prev);

        if (newSelected.has(index)) {
          newSelected.delete(index);
        } else {
          // Check max selections
          if (maxSelections && newSelected.size >= maxSelections) {
            return prev; // Don't allow more selections
          }
          newSelected.add(index);
        }

        return newSelected;
      });
    },
    [maxSelections]
  );

  const handleSubmit = () => {
    const selectedChoicesArray = Array.from(selectedChoices).map((index) => ({
      key: String(index),
      choice: choices[index],
    }));

    setVisible(false);
    setTimeout(() => {
      onSubmit(selectedChoicesArray);
    }, 300);
  };

  const handleGoBack = () => {
    if (onGoBack) {
      setVisible(false);
      setTimeout(() => {
        onGoBack();
      }, 300);
    }
  };

  const isSubmitDisabled = selectedChoices.size < minSelections;
  const selectionCount = selectedChoices.size;

  const getSelectionCountText = () => {
    if (maxSelections) {
      return `${selectionCount}/${maxSelections} selected`;
    }
    if (minSelections > 1) {
      return `${selectionCount} selected (minimum ${minSelections})`;
    }
    return `${selectionCount} selected`;
  };

  return (
    <Fade in={visible} timeout={300} mountOnEnter unmountOnExit>
      <div className={`${classes.choiceContainer} ${className || ''}`}>
        {canGoBack && onGoBack && (
          <IconButton
            className={classes.backButton}
            size="small"
            onClick={handleGoBack}
            aria-label="Go back"
          >
            <ArrowBackIcon className={classes.backButtonIcon} />
          </IconButton>
        )}

        <div className={classes.choicesGrid}>
          {choices.map((choice, index) => (
            <ChoiceElement
              // TODO@theronburger Do this better its breaking things
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              choiceIndex={index}
              choice={choice}
              isSelected={selectedChoices.has(index)}
              generatedColors={generatedColors}
              onToggle={handleChoiceToggle}
              parseTemplate={parseTemplate}
            />
          ))}
        </div>

        <Typography className={classes.selectionCount}>{getSelectionCountText()}</Typography>

        <Button
          variant="contained"
          color="primary"
          className={`${classes.submitButton} ${
            isSubmitDisabled ? classes.submitButtonDisabled : ''
          }`}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {submitButtonText}
        </Button>
      </div>
    </Fade>
  );
};

export default MultipleChoiceSelector;
